import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    TAdminListing,
    TCreateNewRoleReduxParams,
    TEditRoleDetailsReduxParams,
    TGetAdminListingAPIResponse,
    TGetAdminListingFilters,
    TRoleFilter,
    TGetRoleListingAPIResponse,
    TRoleListing,
    TAdminDetails,
    TCreateNewAdminReduxParams,
    TEditAdminDetailsReduxParams,
    TDeleteAdminReduxParams,
    TExportAdminUserListingAPIResponse,
    TExportAdminRoleListingAPIResponse,
} from '~types/feat/admin';

import { TAdminReduxState } from '~types/redux/state';
import { AuthUserStatusEnum, RoleStatusEnum } from '~utils/enums';

export const initialState: TAdminReduxState = {
    loadings: {
        getAdminListing: false,
        createNewAdmin: false,
        editAdminDetails: false,
        deleteAdmin: false,
        getRoleListing: false,
        createNewRole: false,
        editRoleDetails: false,
        deleteRole: false,
        getAllRoles: false,
        exportAdminUserListing: false,
        exportAdminRoleListing: false,
    },
    modals: {
        createNewAdminModalIsOpen: false,
        adminDetailsModalIsOpen: false,
        adminDetailsModalIsEditing: false,
        deleteAdminModalIsOpen: false,
        createNewRoleModalIsOpen: false,
        roleDetailsModalIsOpen: false,
        roleDetailsModalIsEditing: false,
        deleteRoleModalIsOpen: false,
    },
    adminListing: {
        index: 0,
        maxIndex: 0,
        filters: {
            branchCode: '',
            status: '',
            email: '',
        },
        data: [],
    },
    adminDetails: {
        userId: 0,
        sAMAccountName: '',
        displayName: '',
        branchId: 0,
        branchCode: '',
        roleId: 0,
        roleName: '',
        email: '',
        referralCode: '',
        status: AuthUserStatusEnum.Active,
        createdAt: '',
        updatedAt: '',
        lastLoginAt: '',
    },
    roleListing: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    roleDetails: {
        roleId: 0,
        roleName: '',
        status: RoleStatusEnum.Active,
        createdAt: '',
        updatedAt: '',
        access: [],
        allBranchAccess: false,
    },
    allRoles: [],
    adminUserListingForExport: [],
    adminRoleListingForExport: [],
    errors: {
        getAdminListing: '',
        createNewAdmin: '',
        editAdminDetails: '',
        deleteAdmin: '',
        getRoleListing: '',
        createNewRole: '',
        editRoleDetails: '',
        deleteRole: '',
        getAllRoles: '',
        exportAdminUserListing: '',
        exportAdminRoleListing: '',
    },
};

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        adminGetAdminListingAttempt: (state) => {
            state.loadings.getAdminListing = true;
            state.errors.getAdminListing = '';
        },
        adminGetAdminListingSuccess: (state, action: PayloadAction<TGetAdminListingAPIResponse>) => {
            state.loadings.getAdminListing = false;
            state.adminListing = {
                ...state.adminListing,
                ...action.payload,
            };
        },
        adminGetAdminListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAdminListing = false;
            if (action.payload) state.errors.getAdminListing = action.payload;
        },
        adminSetGetAdminListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.adminListing.index = action.payload.index;
        },
        adminSetGetAdminListingFilters: (state, action: PayloadAction<TGetAdminListingFilters>) => {
            state.adminListing.filters = action.payload;
        },
        adminSetAdminListingData: (state, action: PayloadAction<TAdminListing[]>) => {
            state.adminListing.data = action.payload;
        },
        adminSetCreateNewAdminModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createNewAdminModalIsOpen = action.payload;
        },
        adminResetCreateNewAdminState: (state) => {
            state.loadings.createNewAdmin = initialState.loadings.createNewAdmin;
            state.errors.createNewAdmin = initialState.errors.createNewAdmin;
        },
        adminCreateNewAdminAttempt: (state, _action: PayloadAction<TCreateNewAdminReduxParams>) => {
            state.loadings.createNewAdmin = true;
            state.errors.createNewAdmin = '';
        },
        adminCreateNewAdminSuccess: (state) => {
            state.loadings.createNewAdmin = false;
        },
        adminCreateNewAdminFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewAdmin = false;
            if (action.payload) state.errors.createNewAdmin = action.payload;
        },
        adminSetAdminDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.adminDetailsModalIsOpen = action.payload;
        },
        adminSetAdminDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.adminDetailsModalIsEditing = action.payload;
        },
        adminSetAdminDetails: (state, action: PayloadAction<TAdminDetails>) => {
            state.adminDetails = action.payload;
        },
        adminResetEditAdminDetailsState: (state) => {
            state.loadings.editAdminDetails = initialState.loadings.editAdminDetails;
            state.errors.editAdminDetails = initialState.errors.editAdminDetails;
        },
        adminEditAdminDetailsAttempt: (state, _action: PayloadAction<TEditAdminDetailsReduxParams>) => {
            state.loadings.editAdminDetails = true;
            state.errors.editAdminDetails = '';
        },
        adminEditAdminDetailsSuccess: (state) => {
            state.loadings.editAdminDetails = false;
        },
        adminEditAdminDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.editAdminDetails = false;
            if (action.payload) state.errors.editAdminDetails = action.payload;
        },
        adminSetDeleteAdminModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteAdminModalIsOpen = action.payload;
        },
        adminDeleteAdminAttempt: (state, _action: PayloadAction<TDeleteAdminReduxParams>) => {
            state.loadings.deleteAdmin = true;
            state.errors.deleteAdmin = '';
        },
        adminDeleteAdminSuccess: (state) => {
            state.loadings.deleteAdmin = false;
        },
        adminDeleteAdminFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.deleteAdmin = false;
            if (action.payload) state.errors.deleteAdmin = action.payload;
        },
        adminGetRoleListingAttempt: (state) => {
            state.loadings.getRoleListing = true;
            state.errors.getRoleListing = '';
        },
        adminGetRoleListingSuccess: (state, action: PayloadAction<TGetRoleListingAPIResponse>) => {
            state.loadings.getRoleListing = false;
            state.roleListing = {
                ...state.roleListing,
                ...action.payload,
            };
        },
        adminGetRoleListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getRoleListing = false;
            if (action.payload) state.errors.getRoleListing = action.payload;
        },
        adminSetGetRoleListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.roleListing.index = action.payload.index;
        },
        adminSetRoleListingData: (state, action: PayloadAction<TRoleListing[]>) => {
            state.roleListing.data = action.payload;
        },
        adminSetCreateNewRoleModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createNewRoleModalIsOpen = action.payload;
        },
        adminCreateNewRoleAttempt: (state, _action: PayloadAction<TCreateNewRoleReduxParams>) => {
            state.loadings.createNewRole = true;
            state.errors.createNewRole = '';
        },
        adminCreateNewRoleSuccess: (state) => {
            state.loadings.createNewRole = false;
        },
        adminCreateNewRoleFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewRole = false;
            if (action.payload) state.errors.createNewRole = action.payload;
        },
        adminResetCreateNewRoleState: (state) => {
            state.errors.createNewRole = initialState.errors.createNewRole;
            state.loadings.createNewRole = initialState.loadings.createNewRole;
        },
        adminSetCreateNewRoleError: (state, action: PayloadAction<string>) => {
            state.errors.createNewRole = action.payload;
        },
        adminSetRoleDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.roleDetailsModalIsOpen = action.payload;
        },
        adminSetRoleDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.roleDetailsModalIsEditing = action.payload;
        },
        adminSetRoleDetails: (state, action: PayloadAction<TRoleListing>) => {
            state.roleDetails = action.payload;
        },
        adminEditRoleDetailsAttempt: (state, _action: PayloadAction<TEditRoleDetailsReduxParams>) => {
            state.loadings.editRoleDetails = true;
            state.errors.editRoleDetails = '';
        },
        adminEditRoleDetailsSuccess: (state) => {
            state.loadings.editRoleDetails = false;
        },
        adminEditRoleDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.editRoleDetails = false;
            if (action.payload) state.errors.editRoleDetails = action.payload;
        },
        adminResetEditRoleDetailsState: (state) => {
            state.errors.editRoleDetails = initialState.errors.editRoleDetails;
            state.loadings.editRoleDetails = initialState.loadings.editRoleDetails;
        },
        adminSetDeleteRoleModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteRoleModalIsOpen = action.payload;
        },
        adminDeleteRoleAttempt: (state, _action: PayloadAction<TEditRoleDetailsReduxParams>) => {
            state.loadings.deleteRole = true;
            state.errors.deleteRole = '';
        },
        adminDeleteRoleSuccess: (state) => {
            state.loadings.deleteRole = false;
        },
        adminDeleteRoleFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.deleteRole = false;
            if (action.payload) state.errors.deleteRole = action.payload;
        },
        adminResetDeleteRoleState: (state) => {
            state.loadings.deleteRole = initialState.loadings.deleteRole;
            state.errors.deleteRole = initialState.errors.deleteRole;
        },
        adminGetAllRolesAttempt: (state) => {
            state.loadings.getAllRoles = true;
            state.errors.getAllRoles = '';
        },
        adminGetAllRolesSuccess: (state, action: PayloadAction<TRoleFilter[]>) => {
            state.loadings.getAllRoles = false;
            state.allRoles = action.payload;
        },
        adminGetAllRolesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllRoles = false;
            if (action.payload) state.errors.getAllRoles = action.payload;
        },
        adminExportAdminUserListingAttempt: (state) => {
            state.loadings.exportAdminUserListing = true;
            state.errors.exportAdminUserListing = '';
        },
        adminExportAdminUserListingSuccess: (state, action: PayloadAction<TExportAdminUserListingAPIResponse[]>) => {
            state.loadings.exportAdminUserListing = false;
            state.adminUserListingForExport = action.payload;
        },
        adminExportAdminUserListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportAdminUserListing = false;
            if (action.payload) state.errors.exportAdminUserListing = action.payload;
        },
        adminClearAdminUserListingForExport: (state) => {
            state.adminUserListingForExport = initialState.adminUserListingForExport;
        },
        adminExportAdminRoleListingAttempt: (state) => {
            state.loadings.exportAdminRoleListing = true;
            state.errors.exportAdminRoleListing = '';
        },
        adminExportAdminRoleListingSuccess: (state, action: PayloadAction<TExportAdminRoleListingAPIResponse[]>) => {
            state.loadings.exportAdminRoleListing = false;
            state.adminRoleListingForExport = action.payload;
        },
        adminExportAdminRoleListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportAdminRoleListing = false;
            if (action.payload) state.errors.exportAdminRoleListing = action.payload;
        },
        adminClearAdminRoleListingForExport: (state) => {
            state.adminRoleListingForExport = initialState.adminRoleListingForExport;
        },
    },
});

export default {
    actions: adminSlice.actions,
    reducers: adminSlice.reducer,
};
